import React, { useState, useEffect } from 'react';
import './Calendar.css';
import DayModal from './DayModal';

const Calendar = ({ elements, elements2, dateAssociations, setDateAssociations, language, tabNames }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Traductions pour les textes de l'interface
  const translations = {
    fr: {
      months: [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ],
      weekdays: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      today: 'Aujourd\'hui',
      previous: 'Mois précédent',
      next: 'Mois suivant',
      noElements: 'Aucun élément créé',
      hasNotes: 'Contient des notes'
    },
    en: {
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],
      weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      today: 'Today',
      previous: 'Previous month',
      next: 'Next month',
      legendTitle: 'Legend',
      noElements: 'No elements created',
      hasNotes: 'Contains notes'
    }
  };
  
  const t = translations[language];
  
  // Fonction pour naviguer au mois précédent
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  
  // Fonction pour naviguer au mois suivant
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  
  // Fonction pour revenir au mois actuel
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Fonction pour formater une date en chaîne ISO (YYYY-MM-DD)
  const formatDateToISO = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  // Fonction pour vérifier si une date donnée a des éléments associés
  const hasElementsForDate = (date) => {
    const dateKey = formatDateToISO(date);
    return dateAssociations[dateKey] && dateAssociations[dateKey].elementIds && dateAssociations[dateKey].elementIds.length > 0;
  };
  
  // Fonction pour vérifier si une date donnée a des notes
  const hasNotesForDate = (date) => {
    const dateKey = formatDateToISO(date);
    return dateAssociations[dateKey] && dateAssociations[dateKey].notes && dateAssociations[dateKey].notes.trim() !== '';
  };
  
  // Fonction pour obtenir les couleurs des éléments associés à une date
  const getColorsForDate = (date) => {
    const dateKey = formatDateToISO(date);
    if (!dateAssociations[dateKey] || !dateAssociations[dateKey].elementIds) {
      return [];
    }
    
    return dateAssociations[dateKey].elementIds
      .map(id => {
        const element = elements.find(el => el.id === id);
        return element ? element.couleur : null;
      })
      .filter(Boolean);
  };

  const getIconsForDate = (date) => {
    const dateKey = formatDateToISO(date);
    if (!dateAssociations[dateKey] || !dateAssociations[dateKey].element2Ids) {
      return [];
    }
  
    return dateAssociations[dateKey].element2Ids
      .map(id => {
        const element = elements2.find(el => el.id === id);
        const rating = dateAssociations[dateKey].ratings?.[id] || 1;
        return element ? { 
          icon: element.icon, 
          color: element.couleur,
          text: element.texte,
          rating: rating
        } : null;
      })
      .filter(Boolean);
  };
  
  // Fonction pour générer les styles CSS de division de couleurs
  const generateColorDivisions = (colors) => {
    if (!colors || colors.length === 0) return [];

    const divisions = [];
    const count = colors.length;
    
    if (count === 1) {
      // Une seule couleur - remplir tout l'arrière-plan
      divisions.push({
        backgroundColor: colors[0],
        width: '100%',
        height: '100%'
      });
    } else if (count === 2) {
      // Deux couleurs - diviser en deux parties diagonales
      divisions.push({
        backgroundColor: colors[0],
        clipPath: 'polygon(0 0, 100% 0, 0 100%)'
      });
      divisions.push({
        backgroundColor: colors[1],
        clipPath: 'polygon(100% 0, 0 100%, 100% 100%)'
      });
    } else if (count === 3) {
      // Trois couleurs - diviser en trois parties
      divisions.push({
        backgroundColor: colors[0],
        clipPath: 'polygon(0 0, 100% 0, 0 100%)'
      });
      divisions.push({
        backgroundColor: colors[1],
        clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 50% 50%)'
      });
      divisions.push({
        backgroundColor: colors[2],
        clipPath: 'polygon(0 100%, 100% 100%, 100% 66%)'
      });
    } else {
      // Quatre couleurs ou plus - diviser en quatre parties
      divisions.push({
        backgroundColor: colors[0],
        clipPath: 'polygon(0 0, 50% 0, 50% 50%, 0 50%)'
      });
      divisions.push({
        backgroundColor: colors[1],
        clipPath: 'polygon(50% 0, 100% 0, 100% 50%, 50% 50%)'
      });
      divisions.push({
        backgroundColor: colors[2 % count],
        clipPath: 'polygon(0 50%, 50% 50%, 50% 100%, 0 100%)'
      });
      divisions.push({
        backgroundColor: colors[3 % count],
        clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)'
      });
    }
    
    return divisions;
  };
  
  // Fonction pour gérer le clic sur un jour
  const handleDayClick = (date) => {
    setSelectedDate(date);
    setShowModal(true);
  };
  
  // Fonction pour sauvegarder les données modifiées depuis la modale
  const handleSaveFromModal = (date, data) => {
    const dateKey = formatDateToISO(date);
    
    setDateAssociations({
      ...dateAssociations,
      [dateKey]: data
    });
    
    setShowModal(false);
  };
  
  // Fonction pour fermer la modale
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Génération du calendrier pour le mois actuel
  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);
    
    // Jours du mois précédent à afficher
    const daysFromPrevMonth = firstDay.getDay();
    // Total de jours à afficher (y compris les jours du mois précédent et suivant)
    const totalDays = daysFromPrevMonth + lastDay.getDate();
    // Nombre de lignes nécessaires (semaines)
    const totalRows = Math.ceil(totalDays / 7);
    
    // Date d'aujourd'hui
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
    
    const calendarRows = [];
    let dayCounter = 1 - daysFromPrevMonth;
    
    for (let row = 0; row < totalRows; row++) {
      const week = [];
      
      for (let col = 0; col < 7; col++) {
        const currentDate = new Date(year, month, dayCounter);
        const isCurrentMonth = currentDate.getMonth() === month;
        const isToday = isCurrentMonth && currentDate.getDate() === today.getDate() && 
                        today.getMonth() === month && today.getFullYear() === year;
        
        week.push({
          date: currentDate,
          dayOfMonth: currentDate.getDate(),
          isCurrentMonth,
          isToday,
          hasElements: hasElementsForDate(currentDate),
          hasNotes: hasNotesForDate(currentDate),
          colors: getColorsForDate(currentDate)
        });
        
        dayCounter++;
      }
      
      calendarRows.push(week);
    }
    
    return calendarRows;
  };
  
  const calendar = generateCalendar();
  
  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="month-navigation">
          <button onClick={goToPreviousMonth} className="nav-button" aria-label={t.previous}>
            &lt;
          </button>
          <h2>
            {t.months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button onClick={goToNextMonth} className="nav-button" aria-label={t.next}>
            &gt;
          </button>
        </div>
        <button onClick={goToToday} className="today-button">
          {t.today}
        </button>
      </div>
      
      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {t.weekdays.map((day, index) => (
            <div key={index} className="weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {calendar.map((week, rowIndex) => (
            <div key={rowIndex} className="calendar-week">
              {week.map((day, colIndex) => (
                <div
                  key={colIndex}
                  className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''}`}
                  onClick={() => handleDayClick(day.date)}
                >
                  <div className="day-number">{day.dayOfMonth}</div>
                  
                  {day.colors.length > 0 && (
                    <div className="day-color-background">
                      {generateColorDivisions(day.colors).map((style, index) => (
                        <div
                          key={index}
                          className="color-division"
                          style={style}
                        ></div>
                      ))}
                    </div>
                  )}

                  {getIconsForDate(day.date).length > 0 && (
                    <div className="day-badges-container">
                      {getIconsForDate(day.date).map((iconData, index) => (
                        <div
                          key={index}
                          className="day-icon-badge"
                          style={{ backgroundColor: iconData.color }}
                          title={iconData.text}
                        >
                          {iconData.rating}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {day.hasNotes && (
                    <div className="day-note-indicator">
                      📝
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      
      <div className="calendar-legend">
        <h3>{t.legendTitle}</h3>
        {elements.length === 0 ? (
          <p className="no-elements">{t.noElements}</p>
        ) : (
          <>
            <h4>{tabNames.elements1}</h4>
            <ul className="legend-elements">
              {elements.map(element => (
                <li key={element.id} className="legend-element">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: element.couleur }}
                  ></div>
                  <span>{element.texte}</span>
                </li>
              ))}
            </ul>
            
            <h4 style={{ marginTop: '20px' }}>{tabNames.elements2}</h4>
            <ul className="legend-elements">
              {elements2.map(element => (
                <li key={element.id} className="legend-element">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: element.couleur }}
                  ></div>
                  <span>{element.texte}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      
      {showModal && selectedDate && (
        <DayModal
          date={selectedDate}
          elements={elements}
          elements2={elements2} // Pass elements2 to DayModal
          dateAssociations={dateAssociations}
          onSave={handleSaveFromModal}
          onClose={handleCloseModal}
          language={language}
        />
      )}
    </div>
  );
};

export default Calendar;